/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function EventCalendar({ currentDate, events, onPrevMonth, onNextMonth, onEventClick }) {
  const [expandedDay, setExpandedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const calendarData = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days = [];

    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i -= 1) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i),
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day += 1) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day += 1) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day),
      });
    }

    return days;
  }, [year, month]);

  const getEventsForDay = (date) => {
    if (!events || !events.length) return [];

    return events.filter((event) => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);

      return eventStart <= dayEnd && eventEnd >= dayStart;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const monthYearTitle = currentDate.toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="w-full">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">{monthYearTitle}</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={onPrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={onNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 mb-2">
        {WEEKDAYS.map((day) => (
          <div key={day} className="py-2 text-center text-sm font-medium text-muted-foreground">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 rounded-lg border overflow-hidden">
        {calendarData.map((dayData) => {
          const dayEvents = getEventsForDay(dayData.date);
          const hasEvents = dayEvents.length > 0;

          return (
            <div
              key={dayData.date.toISOString()}
              className={cn(
                'min-h-[100px] p-2 border-r border-b transition-colors',
                '[&:nth-child(7n)]:border-r-0',
                !dayData.isCurrentMonth && 'bg-muted/40',
                dayData.isCurrentMonth && 'bg-background',
                hasEvents && dayData.isCurrentMonth && 'hover:bg-muted/50 cursor-pointer',
              )}
            >
              <div className="flex flex-col h-full">
                <span
                  className={cn(
                    'inline-flex h-7 w-7 items-center justify-center rounded-full text-sm',
                    !dayData.isCurrentMonth && 'text-muted-foreground/50',
                    dayData.isCurrentMonth && 'text-foreground',
                    isToday(dayData.date) && 'bg-primary text-primary-foreground font-semibold',
                  )}
                >
                  {dayData.day}
                </span>

                {/* Events */}
                <div className="flex-1 mt-1 space-y-1 overflow-hidden">
                  {dayEvents.slice(0, 2).map((event) => (
                    <button
                      key={event._id}
                      type="button"
                      className={cn(
                        'w-full text-left px-2 py-1 rounded text-xs font-medium truncate',
                        'bg-primary/90 text-primary-foreground hover:bg-primary transition-colors',
                      )}
                      onClick={() => onEventClick(event)}
                    >
                      {event.title}
                    </button>
                  ))}
                  {dayEvents.length > 2 && (
                    <button
                      type="button"
                      className="text-xs text-primary hover:text-primary/80 px-2 font-medium"
                      onClick={() => setExpandedDay({ date: dayData.date, events: dayEvents })}
                    >
                      +{dayEvents.length - 2} more
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Expanded Day Modal */}
      {expandedDay && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setExpandedDay(null)}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            className="bg-background rounded-lg shadow-lg w-full max-w-md max-h-[80vh] overflow-hidden"
            style={{ border: '1px solid hsl(var(--border))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg">
                {expandedDay.date.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setExpandedDay(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto max-h-[60vh]">
              {expandedDay.events.map((event) => (
                <button
                  key={event._id}
                  type="button"
                  className={cn(
                    'w-full text-left px-3 py-2 rounded-lg transition-colors',
                    'bg-primary/10 hover:bg-primary/20',
                  )}
                  style={{ border: '1px solid hsl(var(--border))' }}
                  onClick={() => {
                    setExpandedDay(null);
                    onEventClick(event);
                  }}
                >
                  <p className="font-medium text-sm">{event.title}</p>
                  {event.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {event.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

EventCalendar.propTypes = {
  currentDate: PropTypes.instanceOf(Date).isRequired,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      title: PropTypes.string.isRequired,
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      image: PropTypes.string,
      link: PropTypes.string,
    }),
  ),
  onPrevMonth: PropTypes.func.isRequired,
  onNextMonth: PropTypes.func.isRequired,
  onEventClick: PropTypes.func.isRequired,
};

export default EventCalendar;
